import { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { AudiobookPage, Audiobook } from '@/hooks/use-audiobooks';
import { useAuth } from '@/components/AuthProvider';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';

// Free preview limit for audiobooks (pages beyond this require subscription)
const FREE_AUDIOBOOK_PREVIEW_PAGES = 4;
export interface RoxboxTrack {
  id: string;
  name: string;
  artist: string | null;
  cover_image_url: string;
  audio_url: string;
  video_url: string | null;
  duration_seconds: number | null;
  display_order: number;
  is_active: boolean;
  book_id: number | null;
}

// Unified playable item - can be a track or an audiobook page
export type PlayableItem = 
  | { type: 'track'; data: RoxboxTrack }
  | { type: 'audiobook'; data: AudiobookPage; audiobook: Audiobook };

interface RoxboxPlayerContextType {
  tracks: RoxboxTrack[];
  currentTrack: RoxboxTrack | null;
  currentAudiobookPage: AudiobookPage | null;
  currentAudiobook: Audiobook | null;
  playingType: 'track' | 'audiobook' | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isLooping: boolean;
  isLoading: boolean;
  isMiniPlayerVisible: boolean;
  isNowPlayingOpen: boolean;
  isSubscribed: boolean;
  canSkipNext: boolean; // Whether next button should be enabled
  setNowPlayingOpen: (open: boolean) => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  setTrack: (track: RoxboxTrack, autoPlay?: boolean) => void;
  setAudiobookPage: (page: AudiobookPage, audiobook: Audiobook, autoPlay?: boolean) => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  nextTrack: () => void;
  previousTrack: () => void;
  toggleLoop: () => void;
  clearPlayer: () => void; // Clears the current track/audiobook and stops playback
  onPaywallHit: (() => void) | null;
  setPaywallHandler: (handler: (() => void) | null) => void;
  // Callback for reader to control audio end behavior
  setAudiobookEndedHandler: (handler: (() => boolean) | null) => void;
}

const RoxboxPlayerContext = createContext<RoxboxPlayerContextType | null>(null);

export function RoxboxPlayerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const isSubscribed = subscriptionStatus?.subscription_status === 'active';
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const listenStartRef = useRef<number | null>(null);
  const paywallHandlerRef = useRef<(() => void) | null>(null);
  const audiobookEndedHandlerRef = useRef<(() => boolean) | null>(null);
  const [tracks, setTracks] = useState<RoxboxTrack[]>([]);
  const [currentTrack, setCurrentTrack] = useState<RoxboxTrack | null>(null);
  const [currentAudiobookPage, setCurrentAudiobookPageState] = useState<AudiobookPage | null>(null);
  const [currentAudiobook, setCurrentAudiobook] = useState<Audiobook | null>(null);
  const [playingType, setPlayingType] = useState<'track' | 'audiobook' | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [isLooping, setIsLooping] = useState(false);
  const [isNowPlayingOpen, setIsNowPlayingOpen] = useState(false);

  // Helper to check if a page is behind paywall
  const isPageLocked = useCallback((pageNumber: number, audiobook: Audiobook | null) => {
    if (isSubscribed) return false;
    if (audiobook?.isPreview) return false; // Free audiobooks have no paywall
    return pageNumber > FREE_AUDIOBOOK_PREVIEW_PAGES;
  }, [isSubscribed]);

  // Check if next track would hit paywall
  const canSkipNext = (() => {
    if (playingType !== 'audiobook' || !currentAudiobook || !currentAudiobookPage) return true;
    if (isSubscribed) return true;
    if (currentAudiobook.isPreview) return true; // Free audiobook
    
    const currentIndex = currentAudiobook.pages.findIndex(p => p.id === currentAudiobookPage.id);
    const nextPage = currentAudiobook.pages[currentIndex + 1];
    if (!nextPage) return true; // No next page
    
    return nextPage.pageNumber <= FREE_AUDIOBOOK_PREVIEW_PAGES;
  })();

  const setPaywallHandler = useCallback((handler: (() => void) | null) => {
    paywallHandlerRef.current = handler;
  }, []);

  // Handler for reader to control what happens when audiobook audio ends
  const setAudiobookEndedHandler = useCallback((handler: (() => boolean) | null) => {
    audiobookEndedHandlerRef.current = handler;
  }, []);

  // Fetch tracks with React Query for caching
  const { data: fetchedTracks = [], isLoading: tracksLoading } = useQuery({
    queryKey: ['roxbox-tracks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roxbox_tracks')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - same as books
    gcTime: 30 * 60 * 1000,   // 30 minutes cache
    refetchOnWindowFocus: false,
  });

  // Sync fetched tracks to state (guarded to avoid infinite render loops)
  useEffect(() => {
    setTracks((prev) => {
      // React Query may return a new array reference even when data is unchanged.
      // Guard to avoid setState-on-every-render loops.
      const same =
        prev.length === fetchedTracks.length &&
        prev.every((t, i) => t.id === fetchedTracks[i]?.id);
      return same ? prev : fetchedTracks;
    });
  }, [fetchedTracks]);

  // Use tracksLoading directly to avoid infinite loop
  const isLoading = tracksLoading;

  // Initialize audio element once
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
    }

    const audio = audioRef.current;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration || 0);
    const handleEnded = () => {
      if (isLooping) {
        audio.currentTime = 0;
        audio.play();
      } else if (playingType === 'audiobook' && currentAudiobook && currentAudiobookPage) {
        // Check if reader has registered a handler to control audio end behavior
        if (audiobookEndedHandlerRef.current) {
          const shouldAdvance = audiobookEndedHandlerRef.current();
          if (!shouldAdvance) {
            setIsPlaying(false);
            return; // Reader says don't auto-advance
          }
        }
        
        // Auto-advance to next audiobook page
        const currentIndex = currentAudiobook.pages.findIndex(p => p.id === currentAudiobookPage.id);
        const nextPage = currentAudiobook.pages[currentIndex + 1];
        
        if (nextPage) {
          // Check if next page is behind paywall
          if (isPageLocked(nextPage.pageNumber, currentAudiobook)) {
            setIsPlaying(false);
            // Trigger paywall handler if set
            paywallHandlerRef.current?.();
          } else {
            setAudiobookPageInternal(nextPage, currentAudiobook);
          }
        } else {
          setIsPlaying(false);
        }
      } else if (playingType === 'track') {
        // Auto-advance to next track
        const currentIndex = tracks.findIndex(t => t.id === currentTrack?.id);
        if (currentIndex < tracks.length - 1) {
          setTrackInternal(tracks[currentIndex + 1]);
        } else {
          setIsPlaying(false);
        }
      } else {
        setIsPlaying(false);
      }
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [currentTrack, currentAudiobookPage, currentAudiobook, playingType, isLooping, tracks]);

  // Update Media Session metadata when track or audiobook page changes (for lock screen display)
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    
    if (playingType === 'audiobook' && currentAudiobookPage) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: `Page ${currentAudiobookPage.pageNumber}`,
        artist: currentAudiobookPage.bookTitle,
        album: 'BuxBox Audiobooks',
        artwork: [
          { src: currentAudiobookPage.imageUrl, sizes: '512x512', type: 'image/jpeg' }
        ]
      });
    } else if (currentTrack) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack.name,
        artist: currentTrack.artist || 'BuxBox',
        album: 'BuxBox',
        artwork: [
          { src: currentTrack.cover_image_url, sizes: '512x512', type: 'image/jpeg' }
        ]
      });
    }

    // Set up media session action handlers
    navigator.mediaSession.setActionHandler('play', () => play());
    navigator.mediaSession.setActionHandler('pause', () => pause());
    navigator.mediaSession.setActionHandler('previoustrack', () => previousTrack());
    navigator.mediaSession.setActionHandler('nexttrack', () => nextTrack());
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime !== undefined) seek(details.seekTime);
    });
  }, [currentTrack, currentAudiobookPage, playingType]);

  const setTrackInternal = useCallback((track: RoxboxTrack, autoPlay: boolean = true) => {
    if (audioRef.current) {
      audioRef.current.src = track.audio_url;
      audioRef.current.load();
      setCurrentTrack(track);
      setCurrentAudiobookPageState(null);
      setCurrentAudiobook(null);
      setPlayingType('track');
      setCurrentTime(0);
      if (autoPlay) {
        audioRef.current.play().catch(console.error);
      }
    }
  }, []);

  const setAudiobookPageInternal = useCallback((page: AudiobookPage, audiobook: Audiobook, autoPlay: boolean = true) => {
    if (audioRef.current) {
      // Skip if audio URL is empty - wait for resolved URL
      if (!page.audioUrl) {
        console.log(`⏳ Skipping page ${page.pageNumber} - no audio URL yet`);
        // Update state but don't touch audio element
        setCurrentAudiobookPageState(page);
        setCurrentAudiobook(audiobook);
        setPlayingType('audiobook');
        return;
      }
      
      // Check if we're already playing this exact audio by comparing page ID and audio URL
      // Use endsWith to handle full URL vs relative URL comparison
      const currentSrc = audioRef.current.src;
      const isSamePage = currentAudiobookPage?.pageNumber === page.pageNumber &&
                         currentAudiobookPage?.bookId === page.bookId;
      const isSameAudio = isSamePage && (
        currentSrc === page.audioUrl ||
        currentSrc.endsWith(new URL(page.audioUrl, window.location.origin).pathname)
      );
      
      if (isSameAudio && currentSrc) {
        // Just update state without reloading audio - keeps playback seamless
        setCurrentAudiobookPageState(page);
        setCurrentAudiobook(audiobook);
        setPlayingType('audiobook');
        console.log(`🎵 Same audio URL, skipping reload for page ${page.pageNumber}`);
        return;
      }
      
      console.log(`🔊 Loading new audio for page ${page.pageNumber}: ${page.audioUrl}`);
      
      // Save listen time before switching
      if (listenStartRef.current && currentAudiobook && user) {
        const listenSeconds = Math.floor((Date.now() - listenStartRef.current) / 1000);
        if (listenSeconds > 0) {
          // Fire and forget - save listen time
          supabase
            .from('audiobook_sessions')
            .upsert({
              user_id: user.id,
              book_id: currentAudiobook.id,
              current_page: currentAudiobookPage?.pageNumber || 1,
              total_listen_time_seconds: listenSeconds,
              last_listened_at: new Date().toISOString(),
            }, { onConflict: 'user_id,book_id' })
            .then(() => {
              // Also increment play count
              supabase.rpc('record_audiobook_listen', {
                p_book_id: currentAudiobook.id,
                p_user_id: user.id,
                p_page_number: currentAudiobookPage?.pageNumber || 1,
                p_listen_seconds: listenSeconds
              });
            });
        }
      }
      
      // Stop current audio before loading new source to prevent overlap
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      
      audioRef.current.src = page.audioUrl;
      audioRef.current.load();
      setCurrentAudiobookPageState(page);
      setCurrentAudiobook(audiobook);
      setCurrentTrack(null);
      setPlayingType('audiobook');
      setCurrentTime(0);
      if (autoPlay) {
        audioRef.current.play().catch(console.error);
      }
      
      // Start tracking new page
      listenStartRef.current = Date.now();
    }
  }, [user, currentAudiobook, currentAudiobookPage]);

  const play = useCallback(() => {
    audioRef.current?.play().catch(console.error);
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setVolumeState(newVolume);
    }
  }, []);

  const nextTrack = useCallback(() => {
    if (playingType === 'audiobook' && currentAudiobook && currentAudiobookPage) {
      const currentIndex = currentAudiobook.pages.findIndex(p => p.id === currentAudiobookPage.id);
      const nextPage = currentAudiobook.pages[(currentIndex + 1) % currentAudiobook.pages.length];
      
      // Block if next page is behind paywall
      if (nextPage && isPageLocked(nextPage.pageNumber, currentAudiobook)) {
        paywallHandlerRef.current?.();
        return;
      }
      
      if (nextPage) {
        setAudiobookPageInternal(nextPage, currentAudiobook);
      }
    } else if (currentTrack && tracks.length > 0) {
      const currentIndex = tracks.findIndex(t => t.id === currentTrack.id);
      const nextIndex = (currentIndex + 1) % tracks.length;
      setTrackInternal(tracks[nextIndex]);
    }
  }, [currentTrack, currentAudiobookPage, currentAudiobook, playingType, tracks, setTrackInternal, setAudiobookPageInternal, isPageLocked]);

  const previousTrack = useCallback(() => {
    if (currentTime > 3) {
      seek(0);
      return;
    }
    if (playingType === 'audiobook' && currentAudiobook && currentAudiobookPage) {
      const currentIndex = currentAudiobook.pages.findIndex(p => p.id === currentAudiobookPage.id);
      // Don't allow going back from page 1
      if (currentIndex === 0) {
        seek(0); // Just restart current page
        return;
      }
      const prevIndex = currentIndex - 1;
      setAudiobookPageInternal(currentAudiobook.pages[prevIndex], currentAudiobook);
    } else if (currentTrack && tracks.length > 0) {
      const currentIndex = tracks.findIndex(t => t.id === currentTrack.id);
      const prevIndex = currentIndex === 0 ? tracks.length - 1 : currentIndex - 1;
      setTrackInternal(tracks[prevIndex]);
    }
  }, [currentTrack, currentAudiobookPage, currentAudiobook, playingType, tracks, currentTime, setTrackInternal, setAudiobookPageInternal, seek]);

  const toggleLoop = useCallback(() => {
    setIsLooping(prev => !prev);
  }, []);

  // Clear the player completely (stop and remove current track/audiobook)
  const clearPlayer = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setCurrentTrack(null);
    setCurrentAudiobookPageState(null);
    setCurrentAudiobook(null);
    setPlayingType(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  // Compute if mini player should be visible
  const isMiniPlayerVisible = !!currentTrack || !!currentAudiobookPage;

  return (
    <RoxboxPlayerContext.Provider
      value={{
        tracks,
        currentTrack,
        currentAudiobookPage,
        currentAudiobook,
        playingType,
        isPlaying,
        currentTime,
        duration,
        volume,
        isLooping,
        isLoading,
        isMiniPlayerVisible,
        isNowPlayingOpen,
        isSubscribed,
        canSkipNext,
        setNowPlayingOpen: setIsNowPlayingOpen,
        play,
        pause,
        togglePlay,
        setTrack: setTrackInternal,
        setAudiobookPage: setAudiobookPageInternal,
        seek,
        setVolume,
        nextTrack,
        previousTrack,
        toggleLoop,
        clearPlayer,
        onPaywallHit: paywallHandlerRef.current,
        setPaywallHandler,
        setAudiobookEndedHandler,
      }}
    >
      {children}
    </RoxboxPlayerContext.Provider>
  );
}

export function useRoxboxPlayerContext() {
  const context = useContext(RoxboxPlayerContext);
  if (!context) {
    throw new Error('useRoxboxPlayerContext must be used within RoxboxPlayerProvider');
  }
  return context;
}
